import React, { useMemo, useCallback } from 'react'
import OrderGrinKitsAmount from './OrderGrinKitsAmount'
import OrderGrinKitsAmountMobile from './OrderGrinKitsAmount.mobile'
import useClinic from 'hooks/useClinic'
import usePlanLimits from 'hooks/usePlanLimits'
import { isMobile } from 'utils/mobileUtils'
import { GRIN_SCOPE_MAX_IN_ORDER, GRIN_SCOPE_MIN_IN_ORDER } from 'consts/billingConsts'
import { shouldHideOrderGrinKitsPrice } from './orderGrinKitsUtils'

const OrderGrinKitsAmountContainer = ({
  products,
  shippingFee,
  formErrors,
  onShowProductComparison,
  onQuantityChange
}) => {
  const { canClinicBuyScopes } = useClinic()
  const planLimits = usePlanLimits()
  const canBuyScopes = canClinicBuyScopes()

  const handleChangeQuantity = useCallback(
    ({ type, quantity, onSetQuantity }) => {
      onQuantityChange?.({ type, quantity })
      onSetQuantity(quantity)
    },
    [onQuantityChange]
  )

  const handleClickMinusQuantity = useCallback(
    product => {
      const { quantity, maxQuantity = GRIN_SCOPE_MAX_IN_ORDER } = product
      const newQuantity =
        quantity % GRIN_SCOPE_MIN_IN_ORDER === 0
          ? quantity - GRIN_SCOPE_MIN_IN_ORDER
          : quantity - (quantity % GRIN_SCOPE_MIN_IN_ORDER)
      handleChangeQuantity({ ...product, quantity: Math.max(0, Math.min(newQuantity, maxQuantity)) })
    },
    [handleChangeQuantity]
  )

  const handleClickPlusQuantity = useCallback(
    product => {
      const { quantity, maxQuantity = GRIN_SCOPE_MAX_IN_ORDER } = product
      const newQuantity =
        quantity % GRIN_SCOPE_MIN_IN_ORDER === 0
          ? quantity + GRIN_SCOPE_MIN_IN_ORDER
          : quantity + (GRIN_SCOPE_MIN_IN_ORDER - (quantity % GRIN_SCOPE_MIN_IN_ORDER))
      handleChangeQuantity({ ...product, quantity: Math.min(newQuantity, maxQuantity) })
    },
    [handleChangeQuantity]
  )

  const totalScopesQuantity = useMemo(() => products.reduce((acc, product) => acc + product.quantity, 0), [products])

  const hidePrice = useMemo(
    () => shouldHideOrderGrinKitsPrice({ canClinicBuyScopes: canBuyScopes, products }),
    [canBuyScopes, products]
  )

  return isMobile() ? (
    <OrderGrinKitsAmountMobile
      products={products}
      shippingFee={shippingFee}
      formErrors={formErrors}
      onShowProductComparison={onShowProductComparison}
      handleChangeQuantity={handleChangeQuantity}
      handleClickMinusQuantity={handleClickMinusQuantity}
      handleClickPlusQuantity={handleClickPlusQuantity}
      hidePrice={hidePrice}
      canClinicBuyScopes={canBuyScopes}
      isSubscriptionPlan={planLimits.isSubscriptionPlan}
      hasAnyAmount={totalScopesQuantity > 0}
    />
  ) : (
    <OrderGrinKitsAmount
      products={products}
      shippingFee={shippingFee}
      formErrors={formErrors}
      onShowProductComparison={onShowProductComparison}
      handleChangeQuantity={handleChangeQuantity}
      handleClickMinusQuantity={handleClickMinusQuantity}
      handleClickPlusQuantity={handleClickPlusQuantity}
      hidePrice={hidePrice}
      canClinicBuyScopes={canBuyScopes}
      isSubscriptionPlan={planLimits.isSubscriptionPlan}
      hasAnyAmount={totalScopesQuantity > 0}
    />
  )
}

export default OrderGrinKitsAmountContainer
